import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  helpdeskApi,
  type HelpdeskStatsOverview,
  type HelpdeskTopics,
  type HelpdeskStatsFilters,
  type HelpdeskAssignees,
} from '../../api/helpdesk';
import { listSpaces, type SpaceListItem } from '../../api/auth';
import { useTabParam } from '../../shell/useTabParam';
import { HD_STATUS } from '../HelpdeskPage';
import './helpdesk-stats.css';
import { CompactSelect } from '../../components/arc/compact-select/compact-select';
import { Button } from '../../components/arc/button/button';
import { MetricCard } from '../../components/arc/metric-card/metric-card';
import { ArcPage, ArcPageHeader, ArcPanel, ArcTableFrame, ArcTabs, ArcToolbar } from '../../components/arc/primitives/arc-primitives';
import './helpdesk-stats-arc.css';

function buildMonths(): { ym: string; label: string }[] {
  const out: { ym: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    let label = d.toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' });
    label = label.charAt(0).toUpperCase() + label.slice(1);
    out.push({ ym, label: i === 0 ? `${label} (aktuální)` : label });
  }
  return out;
}

type Tab = 'report' | 'temata' | 'ai';
const HS_TABS: readonly Tab[] = ['report', 'temata', 'ai'];
const STATUS_ORDER = ['novy', 'otevreny', 'ceka_na_uziv', 'vyreseno', 'zavreno', 'spam'];
const LANG_FLAG: Record<string, string> = { cs: '🇨🇿 CZ', sk: '🇸🇰 SK', ro: '🇷🇴 RO', en: '🇬🇧 EN' };

function fmtMinutes(m: number | null): string {
  if (m == null) return '-';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ${m % 60} min`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
}

export function HelpdeskStats({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useTabParam<Tab>('tab', HS_TABS, 'report');
  const [period, setPeriod] = useState('month');
  const [month, setMonth] = useState('');
  const [spaceId, setSpaceId] = useState('');
  const [agent, setAgent] = useState('');
  const [assignees, setAssignees] = useState<HelpdeskAssignees>({ users: [], aiAgents: [] });
  const [spaces, setSpaces] = useState<SpaceListItem[]>([]);
  const months = useMemo(buildMonths, []);

  const filters: HelpdeskStatsFilters = useMemo(
    () => ({
      period,
      month: period === 'month' ? month || undefined : undefined,
      spaceId: spaceId || undefined,
      agent: agent || undefined,
    }),
    [period, month, spaceId, agent],
  );

  useEffect(() => {
    helpdeskApi.assignees().then(setAssignees).catch(() => {});
    listSpaces().then(setSpaces).catch(() => {});
  }, []);

  return (
    <ArcPage className="hs-wrap hs-arc-page">
      <ArcPageHeader eyebrow="HELPDESK" title="Výkon podpory" description="Rychlost, vytížení a témata zákaznických požadavků." meta={<Button type="button" variant="ghost" size="sm" onClick={onClose}>Zpět na Helpdesk</Button>} />
      <ArcToolbar className="hs-arc-tabbar" label="Pohled a filtry statistik" end={<div className="hs-arc-filters">
          <CompactSelect className="hs-filter-select" value={period} onChange={setPeriod} ariaLabel="Období" options={[{ value: 'day', label: 'Den (dnes)' }, { value: 'week', label: 'Týden (tento)' }, { value: 'month', label: 'Měsíc' }, { value: 'all', label: 'Vše' }]} />
          {period === 'month' && (
            <CompactSelect className="hs-filter-select" value={month || months[0]?.ym || ''} onChange={setMonth} ariaLabel="Měsíc" options={months.map((item) => ({ value: item.ym, label: item.label }))} />
          )}
          <CompactSelect className="hs-filter-select" value={spaceId} onChange={setSpaceId} ariaLabel="Space" options={[{ value: '', label: 'Všechny Spaces' }, ...spaces.map((item) => ({ value: String(item.id), label: item.name }))]} />
          <CompactSelect className="hs-filter-select" value={agent} onChange={setAgent} ariaLabel="Agent" options={[{ value: '', label: 'Všichni agenti' }, ...assignees.users.map((item) => ({ value: `user:${item.id}`, label: item.name })), ...assignees.aiAgents.map((item) => ({ value: `ai:${item.id}`, label: item.name }))]} />
      </div>}>
        <ArcTabs items={[{ value: 'report', label: 'Report' }, { value: 'temata', label: 'Témata' }, { value: 'ai', label: 'AI návrhy' }]} value={tab} onChange={setTab} label="Statistiky Helpdesku" />
      </ArcToolbar>

      {tab === 'report' && <ReportTab filters={filters} />}
      {tab === 'temata' && <TopicsTab filters={filters} />}
      {tab === 'ai' && <AiTab />}
    </ArcPage>
  );
}

function ReportTab({ filters }: { filters: HelpdeskStatsFilters }) {
  const [data, setData] = useState<HelpdeskStatsOverview | null>(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => { helpdeskApi.stats(filters).then(setData).catch((e) => setErr((e as { code?: string })?.code ?? 'Chyba')); }, [filters]);
  useEffect(load, [load]);

  if (err) return <div className="hs-err">Chyba: {err}</div>;
  if (!data) return <div className="hs-empty">Načítám…</div>;

  const maxDay = Math.max(1, ...data.timeline.map((p) => p.count));

  return (
    <div>
      <div className="hs-kpis">
        <Kpi label="Tickety celkem" value={data.total} badge="v období" />
        <Kpi label="Vyřešeno" value={data.resolved} badge={`${data.resolvedPct} %`} />
        <Kpi label="Otevřené" value={data.open} badge="aktivní" />
        <Kpi label="Spam" value={data.spam} />
        <Kpi label="Ø první odpověď" value={fmtMinutes(data.avgFirstReplyMinutes)} badge="od přijetí" />
        <Kpi label="Ø do vyřešení" value={fmtMinutes(data.avgResolveMinutes)} badge="jen vyřešené" />
      </div>

      <ArcPanel className="hs-card">
        <div className="hs-card-title">Rozpis dle stavu</div>
        <div className="hs-stacked-bar">
          {STATUS_ORDER.map((s) => {
            const n = data.byStatus[s] ?? 0;
            if (!n || data.total === 0) return null;
            return <div key={s} className="hs-stack-seg" title={`${HD_STATUS[s]?.label}: ${n}`} style={{ width: `${(n * 100) / data.total}%`, background: HD_STATUS[s]?.color }} />;
          })}
        </div>
        <div className="hs-legend">
          {STATUS_ORDER.map((s) => (
            <span key={s} className="hs-legend-item">
              <span className="hs-legend-dot" style={{ background: HD_STATUS[s]?.color }} />
              {HD_STATUS[s]?.label} <b>{data.byStatus[s] ?? 0}</b>
            </span>
          ))}
        </div>
      </ArcPanel>

      <div className="hs-2col">
        <ArcPanel className="hs-card">
          <div className="hs-card-title">Podle schránky</div>
          <ArcTableFrame className="hs-arc-table" sticky="container" label="Tickety podle schránky">
          <table className="hs-table">
            <thead><tr><th>Schránka</th><th>Celkem</th><th>Vyřešeno</th><th>Otevřené</th><th>% vyřeš.</th></tr></thead>
            <tbody>
              {data.perAccount.map((a) => (
                <tr key={a.accountId}><td>{a.name}</td><td>{a.total}</td><td>{a.resolved}</td><td>{a.open}</td><td>{a.resolvedPct} %</td></tr>
              ))}
              {data.perAccount.length === 0 && <tr><td colSpan={5} className="hs-empty">Žádná data.</td></tr>}
            </tbody>
          </table>
          </ArcTableFrame>
        </ArcPanel>
        <ArcPanel className="hs-card">
          <div className="hs-card-title">Podle jazyka</div>
          <ArcTableFrame className="hs-arc-table" sticky="container" label="Tickety podle jazyka">
          <table className="hs-table">
            <tbody>
              {data.perLanguage.map((l) => (
                <tr key={l.language}><td>{LANG_FLAG[l.language] ?? `🌐 ${l.language}`}</td><td className="hs-num">{l.count}</td></tr>
              ))}
              {data.perLanguage.length === 0 && <tr><td className="hs-empty">Žádná data.</td></tr>}
            </tbody>
          </table>
          </ArcTableFrame>
        </ArcPanel>
      </div>

      <ArcPanel className="hs-card">
        <div className="hs-card-title">Aktivita operátorů</div>
        <ArcTableFrame className="hs-arc-table" sticky="container" label="Aktivita operátorů">
        <table className="hs-table">
          <thead><tr><th>Operátor</th><th>Přiřazeno</th><th>Vyřešeno</th><th>Otevřené</th><th>Odpovědi</th><th>Poznámky</th><th>Ø 1. odpověď</th><th>% vyřeš.</th></tr></thead>
          <tbody>
            {(data.perOperator ?? []).map((o) => (
              <tr key={o.userId}>
                <td>{o.name}</td>
                <td>{o.assigned}</td>
                <td>{o.resolved}</td>
                <td>{o.open}</td>
                <td>{o.replies}</td>
                <td>{o.notes}</td>
                <td>{o.avgFirstReplyMinutes != null ? `${o.avgFirstReplyMinutes} min` : '—'}</td>
                <td>{o.resolvedPct} %</td>
              </tr>
            ))}
            {(!data.perOperator || data.perOperator.length === 0) && (
              <tr><td colSpan={8} className="hs-empty">Žádná data.</td></tr>
            )}
          </tbody>
        </table>
        </ArcTableFrame>
      </ArcPanel>

      <ArcPanel className="hs-card">
        <div className="hs-card-title">Vývoj v čase</div>
        <div className="hs-timeline">
          {data.timeline.map((p) => (
            <div key={p.day} className="hs-tl-bar" title={`${p.day}: ${p.count}`}>
              <div className="hs-tl-fill" style={{ height: `${(p.count * 100) / maxDay}%` }} />
              <span className="hs-tl-label">{p.day.slice(8, 10)}.{p.day.slice(5, 7)}.</span>
            </div>
          ))}
          {data.timeline.length === 0 && <div className="hs-empty">Žádná data.</div>}
        </div>
      </ArcPanel>
    </div>
  );
}

function TopicsTab({ filters }: { filters: HelpdeskStatsFilters }) {
  const [data, setData] = useState<HelpdeskTopics | null>(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => { helpdeskApi.statsTopics(filters).then(setData).catch((e) => setErr((e as { code?: string })?.code ?? 'Chyba')); }, [filters]);
  useEffect(load, [load]);

  if (err) return <div className="hs-err">Chyba: {err}</div>;
  if (!data) return <div className="hs-empty">Načítám…</div>;
  const grandTotal = data.taggedTotal + data.untagged;

  return (
    <div>
      <div className="hs-kpis">
        <Kpi label="Otagováno" value={data.taggedTotal} />
        <Kpi label="Bez tagu" value={data.untagged} badge={grandTotal ? `${Math.round((data.untagged * 100) / grandTotal)} %` : undefined} />
      </div>
      <ArcPanel className="hs-card">
        <div className="hs-card-title">Podle tagu</div>
        <ArcTableFrame className="hs-arc-table" sticky="container" label="Tickety podle tagu">
        <table className="hs-table">
          <thead><tr><th>Tag</th><th>Tickety</th><th>Vyřešeno</th><th>Ø doba řeš.</th><th>Manuál / AI</th></tr></thead>
          <tbody>
            {data.topics.map((t) => (
              <tr key={t.tagId}>
                <td><span className="hs-tag-dot" style={{ background: t.color }} /> {t.name}</td>
                <td>{t.total}</td><td>{t.resolved}</td><td>{fmtMinutes(t.avgResolveMinutes)}</td>
                <td>{t.manualCount} / {t.aiCount}</td>
              </tr>
            ))}
            {data.topics.length === 0 && <tr><td colSpan={5} className="hs-empty">Žádné otagované tickety.</td></tr>}
          </tbody>
        </table>
        </ArcTableFrame>
      </ArcPanel>
    </div>
  );
}

function AiTab() {
  const [reason, setReason] = useState('Načítám…');
  useEffect(() => { helpdeskApi.statsAiInsights().then((r) => setReason(r.reason ?? r.status)).catch(() => setReason('Nedostupné')); }, []);
  return (
    <ArcPanel className="hs-card">
      <div className="hs-card-title">AI návrhy (Honza)</div>
      <p className="hs-empty">{reason.includes('Slice 6') ? 'AI návrhy zatím nejsou dostupné.' : reason}</p>
    </ArcPanel>
  );
}

function Kpi({ label, value, badge }: { label: string; value: number | string; badge?: string }) {
  if (typeof value === 'number') return <MetricCard label={label} value={value} context={badge ?? ''} />;
  return <ArcPanel as="article" className="hs-arc-time-kpi"><span>{label}</span><strong>{value}</strong>{badge && <small>{badge}</small>}</ArcPanel>;
}
